import React, { useMemo } from 'react';
import { Loader2 } from 'lucide-react';
import {
    useWhatsAppStatus, useWhatsAppChats, useWhatsAppMessages,
    useConnectWhatsApp, useLogoutWhatsApp, useSendWhatsAppMessage, useWhatsAppSocketSync,
} from '../hooks/useWhatsAppData';
import WhatsAppHeader from './WhatsAppHeader';
import WhatsAppConnectView from './WhatsAppConnectView';
import WhatsAppChatList from './WhatsAppChatList';
import WhatsAppChatThread from './WhatsAppChatThread';

// Referencia estable para el fallback antes de que `chatsQuery`/`messagesQuery` resuelvan --
// `[]` inline en cada render rompía la identidad de la que dependen `filteredChats`/`messages`
// (ambos useMemo).
const EMPTY_LIST = [];

// 02.3-client.md paso b -- extraído en 4 piezas presentacionales (WhatsAppHeader,
// WhatsAppConnectView, WhatsAppChatList, WhatsAppChatThread, más el leaf compartido
// WhatsAppStatusCheck). Este container conserva todo el estado y el data-fetching (useWhatsAppData,
// paso a) -- las hojas no importan clientApi/hooks de datos/toast, solo reciben props.
// Fase 5.15 -- `toast` llega por prop desde `ClientsView` / `OpDetailPanel`.
export default function WhatsAppPanel({ onClose, toast }) {
  const [searchQuery, setSearchQuery] = React.useState('');
  const [activeChat, setActiveChat] = React.useState(null);
  const [inputText, setInputText] = React.useState('');

  const messagesEndRef = React.useRef(null);

  useWhatsAppSocketSync();

  const statusQuery = useWhatsAppStatus();
  const status = statusQuery.data?.status || 'disconnected';
  const qrCode = statusQuery.data?.qr || null;
  const loading = statusQuery.isLoading;

  const chatsQuery = useWhatsAppChats({ enabled: status === 'connected' });
  const chats = chatsQuery.data?.chats || EMPTY_LIST;

  const messagesQuery = useWhatsAppMessages(activeChat?.chat_jid);
  const loadingMsgs = messagesQuery.isFetching;
  const fetchedMessages = messagesQuery.data?.messages || EMPTY_LIST;

  const connectMutation = useConnectWhatsApp();
  const logoutMutation = useLogoutWhatsApp();
  const sendMutation = useSendWhatsAppMessage(activeChat?.chat_jid);
  const sendingMsg = sendMutation.isPending;

  // Burbuja "enviando..." derivada de la mutación misma (mutation.isPending/variables), sin
  // tocar la cache compartida -- reemplaza el `tempMsgId` + matching manual contra el eco del
  // socket que tenía el código anterior (una fuente real de bugs: 2 caminos de reconciliación
  // independientes podían pisarse entre sí). Al resolver, la invalidación de useSendWhatsAppMessage
  // ya trae el mensaje real persistido.
  const messages = useMemo(() => {
    if (!sendMutation.isPending) return fetchedMessages;
    return [...fetchedMessages, {
      msg_id: '__pending__',
      chat_jid: activeChat?.chat_jid,
      from_me: true,
      text_content: sendMutation.variables?.text,
      status: 'pending',
      timestamp: new Date().toISOString(),
    }];
  }, [fetchedMessages, sendMutation.isPending, sendMutation.variables, activeChat?.chat_jid]);

  const startConnection = () => connectMutation.mutate();

  // Scroll to bottom of message thread
  React.useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, activeChat]);

  const handleSelectChat = (chat) => {
    setActiveChat(chat);
  };

  const handleBackToChats = () => {
    setActiveChat(null);
  };

  const handleSend = (e) => {
    if (e) e.preventDefault();
    const text = inputText.trim();
    if (!text || !activeChat || sendingMsg) return;
    setInputText('');
    sendMutation.mutate({ text });
  };

  const handleLogout = () => {
    toast.prompt('¿Seguro que deseas cerrar la sesión y desvincular WhatsApp? Esto limpiará la caché y permitirá realizar una vinculación fresca.', {
      confirmText: 'Desvincular',
      onConfirm: () => logoutMutation.mutate(),
    });
  };

  // Antes
  // se recalculaba en cada render sin `useMemo` — cualquier tecleo del composer (`inputText`)
  // lo disparaba igual sin que `chats`/`searchQuery` hubieran cambiado.
  const filteredChats = useMemo(
    () => chats.filter(c =>
      c.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.chat_jid?.includes(searchQuery)
    ),
    [chats, searchQuery]
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', color: '#e4e4e7' }}>
      <WhatsAppHeader
        activeChat={activeChat}
        onBack={handleBackToChats}
        status={status}
        onClose={onClose}
        onLogout={handleLogout}
        showLoadingBar={loading || loadingMsgs || sendingMsg}
      />

      {/* Body Area */}
      <div style={{ flex: 1, overflowY: 'auto', position: 'relative' }}>
        {loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifySelf: 'center', justifyContent: 'center', height: '100%', gap: 'var(--space-3)', marginTop: '60px' }}>
            <Loader2 size={24} style={{ color: '#f97316', animation: 'spin 1s linear infinite' }} />
            <span style={{ fontSize: '13px', color: 'rgba(255,255,255,0.4)' }}>Cargando módulo...</span>
          </div>
        ) : (status !== 'connected') ? (
          <WhatsAppConnectView status={status} qrCode={qrCode} onConnect={startConnection} />
        ) : !activeChat ? (
          <WhatsAppChatList
            chats={filteredChats}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            onSelectChat={handleSelectChat}
          />
        ) : (
          <WhatsAppChatThread
            messages={messages}
            loadingMsgs={loadingMsgs}
            inputText={inputText}
            onInputChange={setInputText}
            onSend={handleSend}
            sendingMsg={sendingMsg}
            messagesEndRef={messagesEndRef}
          />
        )}
      </div>
    </div>
  );
}
